import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { offerService } from '../../../services/offerService';

const ReportPreview = ({ 
  property, 
  selectedOffers, 
  includePropertyDetails, 
  includeAgentNotes, 
  aiEnabled,
  aiAnalysis,
  isLoadingAi,
  aiError,
  agentNameDisplay,
  buyerNameDisplay
}) => {
  const [contractUrls, setContractUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(false);

  // Fetch contract PDF URLs with 1-week expiration when offers change
  useEffect(() => {
    const fetchContractUrls = async () => {
      if (!selectedOffers || selectedOffers?.length === 0) {
        setContractUrls({});
        return;
      }

      setLoadingUrls(true);
      const urls = {};

      for (const offer of selectedOffers) {
        if (offer?.contractPdfUrl) {
          const { data, error } = await offerService?.getContractPdfUrlForReport(offer?.contractPdfUrl);
          if (data && !error) {
            urls[offer?.id] = data;
          }
        }
      }

      setContractUrls(urls);
      setLoadingUrls(false);
    };

    fetchContractUrls();
  }, [selectedOffers]);

  if (!property || selectedOffers?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 md:p-12 lg:p-16 flex flex-col items-center justify-center min-h-[400px]">
        <Icon name="FileText" size={64} className="text-muted-foreground mb-4" />
        <p className="text-lg text-muted-foreground text-center">
          Select offers to preview report
        </p>
        <p className="text-sm text-muted-foreground text-center mt-2">
          Choose at least one offer to see the comparison report preview
        </p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const formatNameByPrivacy = (name, displayMode) => {
    if (!name) return 'N/A';
    
    if (displayMode === 'hidden') {
      return '[Hidden]';
    }
    
    if (displayMode === 'initials') {
      const nameParts = name?.trim()?.split(/\s+/);
      if (nameParts?.length === 1) {
        return nameParts?.[0]?.charAt(0)?.toUpperCase() + '.';
      }
      return nameParts?.map(part => part?.charAt(0)?.toUpperCase() + '.')?.join(' ');
    }
    
    // displayMode === 'full'
    return name;
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="bg-primary text-primary-foreground p-4 md:p-6">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="FileText" size={24} />
          <h3 className="text-lg md:text-xl font-semibold">Offer Comparison Report</h3>
        </div>
        <p className="text-sm opacity-90">Generated on {new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        {includePropertyDetails && (
          <div className="space-y-4">
            <h4 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Home" size={20} />
              Property Information
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {property?.image && (
                <div className="lg:col-span-1">
                  <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={property?.image}
                      alt={property?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
              <div className={`${property?.image ? 'lg:col-span-2' : 'lg:col-span-3'} space-y-3`}>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="text-base font-medium text-foreground">{property?.address}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Owner(s)</p>
                    <p className="text-base font-medium text-foreground">{property?.ownerNames}</p>
                  </div>
                  {property?.listingPrice && (
                    <div>
                      <p className="text-sm text-muted-foreground">Listing Price</p>
                      <p className="text-base font-medium text-foreground data-text">{formatCurrency(property?.listingPrice)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {aiEnabled && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6 space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-accent/20 rounded-lg">
                <Icon name="Sparkles" size={20} className="text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-foreground mb-2">AI-Generated Summary</h4>
                {isLoadingAi ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Generating AI insights...</span>
                  </div>
                ) : aiError ? (
                  <div className="flex items-start gap-2 text-sm text-warning">
                    <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{aiError}</span>
                  </div>
                ) : aiAnalysis?.summary ? (
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {aiAnalysis?.summary}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Select offers to generate AI-powered insights.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <h4 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="BarChart3" size={20} />
            Offer Comparison
          </h4>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 text-sm font-semibold text-foreground border border-border">Details</th>
                  {selectedOffers?.map((offer, index) => (
                    <th key={offer?.id} className="text-left p-3 text-sm font-semibold text-foreground border border-border whitespace-nowrap">
                      Offer {index + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Agent</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">
                      {formatNameByPrivacy(offer?.buyerAgent, agentNameDisplay)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Buyer Name</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">
                      {formatNameByPrivacy(offer?.buyerName, buyerNameDisplay)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Offer Price</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border data-text">{formatCurrency(offer?.offerPrice)}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Closing Date</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border whitespace-nowrap">{formatDate(offer?.closingDate)}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Financing Type</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">{offer?.financingType}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Total Deposit</td>
                  {selectedOffers?.map(offer => {
                    const totalDeposit = (parseFloat(offer?.downPayment) || 0) + (parseFloat(offer?.earnestMoney) || 0);
                    const percentage = offer?.offerPrice ? ((totalDeposit / parseFloat(offer?.offerPrice)) * 100)?.toFixed(2) : 0;
                    return (
                      <td key={offer?.id} className="p-3 text-sm text-foreground border border-border data-text">
                        {formatCurrency(totalDeposit)}
                        <span className="text-xs text-muted-foreground ml-1">({percentage}%)</span>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Mortgage amount</td>
                  {selectedOffers?.map(offer => {
                    const totalDeposit = (parseFloat(offer?.downPayment) || 0) + (parseFloat(offer?.earnestMoney) || 0);
                    const offerPrice = parseFloat(offer?.offerPrice) || 0;
                    const mortgageAmount = offerPrice - totalDeposit;
                    const percentage = offerPrice ? ((mortgageAmount / offerPrice) * 100)?.toFixed(2) : 0;
                    return (
                      <td key={offer?.id} className="p-3 text-sm text-foreground border border-border data-text">
                        {formatCurrency(mortgageAmount)}
                        <span className="text-xs text-muted-foreground ml-1">({percentage}%)</span>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Amount to Seller</td>
                  {selectedOffers?.map(offer => {
                    const offerPrice = parseFloat(offer?.offerPrice) || 0;
                    const propertyCommission = parseFloat(property?.commissionPercentage) || 0;
                    const buyersAgentCommission = parseFloat(offer?.buyersAgentCommission) || 0;
                    const sellerConcessions = parseFloat(offer?.sellerConcessions) || 0;
                    
                    // Calculate commission amounts in dollars
                    const listingCommissionAmount = offerPrice * (propertyCommission / 100);
                    const buyersAgentCommissionAmount = offerPrice * (buyersAgentCommission / 100);
                    const totalCommissions = listingCommissionAmount + buyersAgentCommissionAmount;
                    
                    // Subtract total commissions and seller concessions from offer price
                    const amountToSeller = offerPrice - totalCommissions - sellerConcessions;
                    
                    return (
                      <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">
                        <div className="space-y-1">
                          <div className="data-text">{formatCurrency(amountToSeller)}</div>
                          {(propertyCommission > 0 || buyersAgentCommission > 0 || sellerConcessions > 0) && (
                            <div className="text-xs text-muted-foreground space-y-0.5">
                              {propertyCommission > 0 && (
                                <div>Listing: -{formatCurrency(listingCommissionAmount)} ({propertyCommission}%)</div>
                              )}
                              {buyersAgentCommission > 0 && (
                                <div>Buyer's Agent: -{formatCurrency(buyersAgentCommissionAmount)} ({buyersAgentCommission}%)</div>
                              )}
                              {sellerConcessions > 0 && (
                                <div>Seller Concessions: -{formatCurrency(sellerConcessions)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Lender</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">{offer?.lender || 'N/A'}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Contingencies</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">
                      {offer?.contingencies?.length > 0 ? offer?.contingencies?.join(', ') : 'None'}
                    </td>
                  ))}
                </tr>
                {includeAgentNotes && (
                  <tr>
                    <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Notes</td>
                    {selectedOffers?.map(offer => (
                      <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">
                        {offer?.agentNotes || 'No provisions'}
                      </td>
                    ))}
                  </tr>
                )}
                <tr>
                  <td className="p-3 text-sm font-medium text-muted-foreground border border-border bg-muted/50">Contract PDF</td>
                  {selectedOffers?.map(offer => (
                    <td key={offer?.id} className="p-3 text-sm text-foreground border border-border">
                      {loadingUrls ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="Loader" size={14} className="animate-spin" />
                          <span>Loading...</span>
                        </div>
                      ) : contractUrls?.[offer?.id] ? (
                        <a
                          href={contractUrls?.[offer?.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
                        >
                          <Icon name="FileText" size={16} />
                          <span>View Contract</span>
                          <Icon name="ExternalLink" size={14} />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">No contract uploaded</span>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {aiEnabled && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-6 space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-accent/20 rounded-lg">
                <Icon name="Lightbulb" size={20} className="text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-foreground mb-2">Key Differences</h4>
                {isLoadingAi ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Analyzing key differences...</span>
                  </div>
                ) : aiError ? (
                  <div className="flex items-start gap-2 text-sm text-warning">
                    <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Unable to generate key differences</span>
                  </div>
                ) : aiAnalysis?.keyDifferences && aiAnalysis?.keyDifferences?.length > 0 ? (
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {aiAnalysis?.keyDifferences?.map((difference, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{difference}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Select offers to see key differences.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {aiEnabled && aiAnalysis?.dealRisks && aiAnalysis?.dealRisks?.length > 0 && (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 md:p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-warning/20 rounded-lg">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-foreground mb-3">Deal Risk Assessment</h4>
                {isLoadingAi ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Analyzing deal risks...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aiAnalysis?.dealRisks?.map((riskData, index) => {
                      const offer = selectedOffers?.[riskData?.offerNumber - 1];
                      const riskColorClass = 
                        riskData?.riskLevel === 'High' ? 'text-error' :
                        riskData?.riskLevel === 'Medium'? 'text-warning' : 'text-success';
                      
                      return (
                        <div key={index} className="bg-card rounded-lg p-4 border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-semibold text-foreground">
                              Offer {riskData?.offerNumber}: {offer?.buyerName}
                            </h5>
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${riskColorClass} bg-current/10`}>
                              {riskData?.riskLevel} Risk
                            </span>
                          </div>
                          <ul className="space-y-1.5 text-sm text-muted-foreground">
                            {riskData?.risks?.map((risk, riskIndex) => (
                              <li key={riskIndex} className="flex items-start gap-2">
                                <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                                <span>{risk}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {aiEnabled && aiAnalysis?.financingConcerns && aiAnalysis?.financingConcerns?.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 md:p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Icon name="DollarSign" size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-foreground mb-3">Financing Concerns</h4>
                {isLoadingAi ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Evaluating financing strength...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aiAnalysis?.financingConcerns?.map((financingData, index) => {
                      const offer = selectedOffers?.[financingData?.offerNumber - 1];
                      const strengthColorClass = 
                        financingData?.strengthRating === 'Strong' ? 'text-success' :
                        financingData?.strengthRating === 'Moderate'? 'text-warning' : 'text-error';
                      
                      return (
                        <div key={index} className="bg-card rounded-lg p-4 border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-semibold text-foreground">
                              Offer {financingData?.offerNumber}: {offer?.buyerName}
                            </h5>
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${strengthColorClass} bg-current/10`}>
                              {financingData?.strengthRating} Financing
                            </span>
                          </div>
                          <ul className="space-y-1.5 text-sm text-muted-foreground">
                            {financingData?.concerns?.map((concern, concernIndex) => (
                              <li key={concernIndex} className="flex items-start gap-2">
                                <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                                <span>{concern}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {aiEnabled && aiAnalysis?.sellerGoalAlignment && aiAnalysis?.sellerGoalAlignment?.length > 0 && (
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 md:p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Icon name="Target" size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-foreground mb-3">Seller Goal Alignment</h4>
                {isLoadingAi ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Analyzing goal alignment...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aiAnalysis?.sellerGoalAlignment?.map((alignmentData, index) => {
                      const offer = selectedOffers?.[alignmentData?.offerNumber - 1];
                      const fitColorClass = 
                        alignmentData?.overallFit === 'Excellent' ? 'text-success' :
                        alignmentData?.overallFit === 'Good' ? 'text-blue-600 dark:text-blue-400' :
                        alignmentData?.overallFit === 'Fair'? 'text-warning' : 'text-error';
                      
                      return (
                        <div key={index} className="bg-card rounded-lg p-4 border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-semibold text-foreground">
                              Offer {alignmentData?.offerNumber}: {offer?.buyerName}
                            </h5>
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${fitColorClass} bg-current/10`}>
                              {alignmentData?.overallFit} Fit
                            </span>
                          </div>
                          <ul className="space-y-1.5 text-sm text-muted-foreground">
                            {alignmentData?.alignmentFactors?.map((factor, factorIndex) => (
                              <li key={factorIndex} className="flex items-start gap-2">
                                <Icon name="CheckCircle" size={14} className="mt-0.5 flex-shrink-0" />
                                <span>{factor}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-muted p-4 md:p-6 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          This report is for informational purposes only and does not constitute legal or financial advice. All information should be verified independently.
        </p>
      </div>
    </div>
  );
};

export default ReportPreview;